import { Box, HStack, ScrollView, Text, VStack } from 'native-base'
import { TouchableOpacity, Platform } from 'react-native'
import { ParentVideoList, type SpecialLecture, StudentVideoList, baseMaterialURL, baseVideoURL } from './video-list'
import Header from 'app/features/component/header/Header'
import BackButton from 'app/features/component/BackButton'
import SafeAreaFlex from 'app/features/component/safe-area-flex/SafeAreaFlex'
import colors from 'app/colors'
import Svg from 'app/features/component/svg'
import * as FileSystem from 'expo-file-system'
import { truncateText } from 'app/utils/format'
import { useRouter } from 'next/router'
import { PATH } from '@bridge/core/constants/route-path'
import LoadingSpinner from 'app/features/component/LoadingSpinner'

export default function SpecialLecturePlayer () {
	const router = useRouter()

	if (!router.isReady) {
		return <LoadingSpinner />
	}

	const selectedTab = router.query.selectedTab as string
	const selectedVideoIndexString = router.query.selectedVideoIndex as string

	const selectedVideoIndex = Number.parseInt(selectedVideoIndexString)
	const videoList: SpecialLecture[] = selectedTab === 'STUDENT' ? StudentVideoList : ParentVideoList
	const selectedVideo = videoList[selectedVideoIndex]

	async function goToPlayVideo (selectedVideoIndex: number) {
		await router.push({
			pathname: PATH.SPECIAL_LECTURE_PLAYER,
			query: {
				selectedTab,
				selectedVideoIndex,
			},
		})
	}

	async function materialFileDownload () {
		if (!selectedVideo) {
			return <></>
		}

		const downloadURL = `${baseMaterialURL}${selectedVideo.materialName}`

		if (Platform.OS === 'web') {
			const res = await fetch(downloadURL, {
				method: 'get',
				mode: 'no-cors',
				referrerPolicy: 'no-referrer',
			})
			const blob = await res.blob()

			const aElement = document.createElement('a')
			aElement.setAttribute('download', selectedVideo.krMaterialName)
			const href = URL.createObjectURL(blob)
			aElement.href = href
			aElement.setAttribute('target', '_blank')
			aElement.click()
			URL.revokeObjectURL(href)
		} else {
			const documentDirectory = FileSystem.documentDirectory ?? ''
			await FileSystem.downloadAsync(
				downloadURL,
				documentDirectory + selectedVideo.materialName,
			)
		}
	}

	if (!selectedVideo) {
		return <></>
	}

	return (
		<VStack
			bgColor={'singletons.white'}
			maxWidth="500px"
			alignSelf="center"
			w="100%"
		>
			<Header
				title={`${selectedVideoIndex + 1}강 ${truncateText(
					selectedVideo.title,
					15,
				)}`}
				leftButton={<BackButton />}
			/>
			<SafeAreaFlex>
				<VStack>
					<Box width="full">
						<video
							controls
							style={{ maxHeight: '281px' }}
						>
							<source src={`${baseVideoURL}${selectedVideo.videoName}`} type='video/mp4' />
						</video>
					</Box>
					<VStack>
						<VStack>
							<VStack p="24px">
								<Text size="p_s/500" color={colors.grey600}>
									{selectedVideoIndex + 1}강
								</Text>
								<Text
									mt="12px"
									size="h6/700"
									color={colors.grey900}
								>
									{selectedVideo.title}
								</Text>
								<Text
									mt="8px"
									mb="24px"
									size="p_m/500"
									color={colors.grey800}
								>
									{selectedVideo.description}
								</Text>
								<TouchableOpacity
									onPress={materialFileDownload}
								>
									<HStack
										py="12px"
										height="48px"
										borderWidth="1px"
										borderRadius="12px"
										borderColor={colors.grey200}
										justifyContent="center"
									>
										<HStack alignItems="center">
											<Text
												size="btn_l/700"
												color={colors.grey500}
											>
												자료 받기
											</Text>
											<Svg
												style={{ marginLeft: '8px' }}
												width="25px"
												height="24px"
												asset={require('app/assets/special-lecture/ic_download.svg')}
												src="/special-lecture/ic_download.svg"
											/>
										</HStack>
									</HStack>
								</TouchableOpacity>
							</VStack>
							<Box
								mt="16px"
								backgroundColor={colors.grey100}
								height="10px"
							/>
							<VStack p="24px">
								<Text size="btn_l/700" color={colors.grey900}>
									다음 특강
								</Text>
								{videoList.slice(selectedVideoIndex + 1)
									.length > 0
									? (
										<ScrollView mt="24px">
											<VStack>
												{videoList
													.slice(selectedVideoIndex + 1)
													.map((videoInfo, index) => (
														<TouchableOpacity
															key={
																index +
															selectedVideoIndex
															}
															onPress={async () => {
																await goToPlayVideo(
																	index +
																	1 +
																	selectedVideoIndex,
																)
															}}
														>
															<VStack>
																<HStack
																	py="8px"
																	justifyContent="space-between"
																>
																	<HStack>
																		<Svg
																			width="24px"
																			height="24px"
																			asset={require('app/assets/special-lecture/ic_play.svg')}
																			src="/special-lecture/ic_play.svg"
																		/>
																		<Text
																			ml="8px"
																			width="36px"
																			size="p_s/500"
																			color={
																				colors.grey900
																			}
																		>
																			{index +
																			1 +
																			selectedVideoIndex +
																			1}
																		강
																		</Text>
																		<Text
																			size="p_s/500"
																			color={
																				colors.grey900
																			}
																		>
																			{truncateText(
																				videoInfo.title,
																				20,
																			)}
																		</Text>
																	</HStack>
																	<Text
																		size="p_s/600"
																		color={
																			colors.grey500
																		}
																	>
																		{
																			videoInfo.playTime
																		}
																	</Text>
																</HStack>
																<Box
																	height="1px"
																	backgroundColor={
																		colors.grey200
																	}
																/>
															</VStack>
														</TouchableOpacity>
													))}
											</VStack>
										</ScrollView>
									)
									: (
										<VStack mt="20px" alignItems="center">
											<Text
												size="p_s/500"
												color={colors.grey900}
											>
											다음 특강내용이 없어요
											</Text>
										</VStack>
									)}
							</VStack>
						</VStack>
					</VStack>
				</VStack>
			</SafeAreaFlex>
		</VStack>
	)
}
